// BookingModal — confirms marking a direct lead as Booked, with an opt-in
// (pre-checked) checkbox to also send the customer a confirmation email.
//
// Per-lead consent: there is no persistent setting for the email checkbox.
// The user re-decides each time. Default is "yes, send" because most of
// the time the artist hasn't sent their own confirmation yet, and the
// customer benefits from the closure of a definitive "you're booked"
// message.
//
// The component is presentational. It receives:
//   open                - whether to render
//   leadName            - display name for the confirm question
//   busy                - true while the parent is performing the booking
//                         action (disables both buttons)
//   onCancel()          - close without booking
//   onConfirm({sendEmail}) - parent runs the actual status flip + email
//
// All side effects (status change, email fire, error toasting) live in
// the parent. This file is just UI + a checkbox state.

import React, { useEffect, useState } from "react";

// Workaround classes for the themed Confirm / Cancel modal buttons.
// The rules they're guarding against are `button { background: #111827 }` in
// src/styles.css (no !important) and `.tattoo-app button { ... }` in
// tattoo-ui.css (scoped to the editor, doesn't reach this modal). Class
// specificity already beats element specificity, so the !important markers
// are technically redundant — kept defensively until a visual sweep confirms.
const modalButtonStyles = `
.tt-booking-btn-primary {
  background: #10b981 !important;
  color: #0a0a0a !important;
}
.tt-booking-btn-primary:hover:not(:disabled) {
  background: #34d399 !important;
}
.tt-booking-btn-primary:disabled {
  background: #0d8a64 !important;
  cursor: not-allowed !important;
}
.tt-booking-btn-secondary {
  background: transparent !important;
  color: #d7d7d7 !important;
}
.tt-booking-btn-secondary:hover:not(:disabled) {
  background: #1a1a1a !important;
}
`;

export default function BookingModal({
  open,
  leadName,
  busy,
  onCancel,
  onConfirm,
}) {
  const [sendEmail, setSendEmail] = useState(true);

  // Reset the checkbox to "checked" each time the modal re-opens. Keeps
  // the per-lead consent semantics — the user can't accidentally inherit
  // their last lead's choice.
  useEffect(() => {
    if (open) setSendEmail(true);
  }, [open]);

  // Close on Escape. We do this here (not in the parent) so the parent
  // doesn't have to know whether the modal is mounted.
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape" && !busy) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const displayName = leadName?.trim() || "this customer";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tt-booking-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 200000,
      }}
      // Click outside to dismiss; ignore clicks inside the dialog.
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <style>{modalButtonStyles}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111111",
          border: "1px solid #1f1f1f",
          borderRadius: 16,
          padding: "24px 24px 18px",
          maxWidth: 460,
          width: "100%",
          color: "#eaeaea",
          boxShadow: "0 24px 48px rgba(0,0,0,0.6)",
        }}
      >
        <h2
          id="tt-booking-title"
          style={{
            fontSize: 18,
            fontWeight: 600,
            margin: "0 0 12px",
            letterSpacing: "0.01em",
          }}
        >
          Mark {displayName} as booked?
        </h2>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "12px 14px",
            border: "1px solid #1f1f1f",
            borderRadius: 12,
            background: "#0f0f0f",
            cursor: busy ? "not-allowed" : "pointer",
            marginTop: 6,
          }}
        >
          <input
            type="checkbox"
            checked={sendEmail}
            disabled={busy}
            onChange={(e) => setSendEmail(e.target.checked)}
            style={{
              marginTop: 3,
              width: 16,
              height: 16,
              accentColor: "#10b981",
              cursor: busy ? "not-allowed" : "pointer",
            }}
          />
          <span style={{ flex: 1, lineHeight: 1.45 }}>
            <span style={{ fontSize: 14, color: "#eaeaea", fontWeight: 600 }}>
              Send {displayName} a confirmation email
            </span>
            <span
              style={{
                display: "block",
                fontSize: 13,
                color: "#a8a8a8",
                marginTop: 4,
              }}
            >
              Lets the customer know you've confirmed their booking and
              you'll be in touch about scheduling.
            </span>
          </span>
        </label>

        <div
          style={{
            marginTop: 22,
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            type="button"
            className="tt-booking-btn-secondary"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: "10px 16px",
              border: "1px solid #2a2a2a",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tt-booking-btn-primary"
            onClick={() => onConfirm({ sendEmail })}
            disabled={busy}
            style={{
              padding: "10px 18px",
              border: "none",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: busy ? "not-allowed" : "pointer",
              letterSpacing: "0.02em",
            }}
          >
            {busy ? "Marking…" : "Mark as booked"}
          </button>
        </div>
      </div>
    </div>
  );
}
